import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Star, Check, X, User, Calendar, Trash2, AlertCircle, Upload, Image as ImageIcon, ChevronUp, ChevronDown } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { useLocalized } from "@/hooks/useLocalized";
import { LocalizedContent } from "@/types";

interface Review {
  id: string;
  rating: number;
  comment: LocalizedContent;
  reviewer_name: string;
  is_approved: boolean;
  created_at: string;
}

export function ReviewsManager() {
  const { getLocalized } = useLocalized();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false); // Added from instruction
  const { toast } = useToast();
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data as any || []); // Cast to any to avoid type mismatch with Supabase generated types if they are outdated
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast({
        title: "Error",
        description: "Failed to fetch reviews",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateReviewStatus = async (reviewId: string, isApproved: boolean) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .update({ is_approved: isApproved })
        .eq('id', reviewId);

      if (error) throw error;

      setReviews(reviews.map(r =>
        r.id === reviewId ? { ...r, is_approved: isApproved } : r
      ));

      toast({
        title: isApproved ? "Review approved" : "Review rejected",
        description: `Review has been ${isApproved ? 'approved' : 'hidden'}`,
      });
    } catch (error) {
      console.error('Error updating review status:', error);
      toast({
        title: "Error",
        description: "Failed to update review status",
        variant: "destructive",
      });
    }
  };

  const deleteReview = async (reviewId: string) => {
    if (!confirm('Are you sure you want to permanently delete this review?')) return;

    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;

      setReviews(reviews.filter(r => r.id !== reviewId));

      toast({
        title: "Review deleted",
        description: "Review has been permanently deleted",
      });
    } catch (error) {
      console.error('Error deleting review:', error);
      toast({
        title: "Error",
        description: "Failed to delete review",
        variant: "destructive",
      });
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const pendingReviews = reviews.filter(r => !r.is_approved);
  const approvedReviews = reviews.filter(r => r.is_approved);

  const renderReviewCard = (review: Review) => (
    <Card key={review.id} className="overflow-hidden border-l-4 shadow-sm hover:shadow-md transition-shadow"
      style={{ borderLeftColor: review.is_approved ? '#22c55e' : '#eab308' }}
    >
      <CardHeader className="bg-muted/30 pb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-muted-foreground" />
              {review.reviewer_name}
            </CardTitle>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex">{renderStars(review.rating)}</div>
              <Badge variant={review.is_approved ? "default" : "secondary"} className={review.is_approved ? "bg-green-600 hover:bg-green-700" : "bg-yellow-500 hover:bg-yellow-600 text-white"}>
                {review.is_approved ? "Approved" : "Pending"}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground bg-background border px-2 py-1 rounded">
            <Calendar className="h-3 w-3" />
            {new Date(review.created_at).toLocaleDateString()}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="bg-muted/30 p-4 rounded-md text-sm italic text-muted-foreground">
          "{getLocalized(review.comment)}"
        </div>
      </CardContent>
      <CardFooter className="bg-muted/10 border-t p-3 flex justify-end gap-2">
        {!review.is_approved && (
          <Button size="sm" onClick={() => updateReviewStatus(review.id, true)} className="bg-green-600 hover:bg-green-700 text-white h-8 gap-1">
            <Check className="h-3.5 w-3.5" /> Approve
          </Button>
        )}
        {review.is_approved && (
          <Button size="sm" variant="outline" onClick={() => updateReviewStatus(review.id, false)} className="h-8 gap-1">
            <X className="h-3.5 w-3.5" /> Reject
          </Button>
        )}
        {hasPermission('delete_reviews') && (
          <Button size="sm" variant="destructive" onClick={() => deleteReview(review.id)} className="h-8 gap-1">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        )}
      </CardFooter>
    </Card>
  );

  const renderEmptyState = (type: string) => (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
          <AlertCircle className="h-6 w-6" />
        </div>
        <h3 className="text-lg font-semibold mb-1">No {type} reviews</h3>
        <p className="text-sm">No reviews found in this category.</p>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Reviews</h2>
          <p className="text-muted-foreground mt-1">Moderate and manage customer reviews.</p>
        </div>
        <div className="flex items-center gap-2 bg-muted p-1 rounded-lg text-sm font-medium">
          <span className="px-3 py-1 bg-background shadow-sm rounded-md">{reviews.length} Total</span>
          <span className="px-3 py-1 text-yellow-600">{pendingReviews.length} Pending</span>
        </div>
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4 mt-6">
          {pendingReviews.length > 0 ? pendingReviews.map(renderReviewCard) : renderEmptyState('pending')}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4 mt-6">
          {approvedReviews.length > 0 ? approvedReviews.map(renderReviewCard) : renderEmptyState('approved')}
        </TabsContent>

        <TabsContent value="all" className="space-y-4 mt-6">
          {reviews.length > 0 ? reviews.map(renderReviewCard) : renderEmptyState('any')}
        </TabsContent>
      </Tabs>
    </div>
  );
}